import React, { useContext, useEffect, useState } from 'react';
import PropTypes from "prop-types";
import AvailabilityButton from "../../Buttons/AvailabilityButton";
import { isSameDay } from "date-fns";
import AvailabilityButtonConsecutive from "../../Buttons/AvailabilityButtonConsecutive";
import { dateFromState } from "../../../Helpers/DateTimeHelper";
import { getEarlierSlot, getLaterSlot } from "../../../Common/Availability/Availability";
import { checkAvailability } from "../../../Modules/Bookings/Requests/BookingFormRequests";
import { BookingFormContext } from "../../../Modules/Bookings/Forms/BookingFormProvider";

// TODO: Fix prop drilling and use more parameters directly from BookingFormProvider
const AmenityConsecutiveAvailabilityInput = (props) => {

    const {
        editBookingId
    } = useContext( BookingFormContext );

    const [canExtend, setCanExtend] = useState({earlier: false, later: false});

    // Clear previously selected slot if availability changes
    useEffect(() => {
        if (!props.availability || !props.availability.availableIntervals.length > 0) {
            clearAllSlots();
        }
    }, [props.availability]);

    useEffect(() => {
        if (props.selectedInterval) {
            const fetchData = async () => {
                setCanExtend({earlier: null, later: null});
                const availabilityCheck = await checkAvailability(
                    props.selectedInterval,
                    props.bookingConfig,
                    editBookingId
                );
                setCanExtend({
                    earlier: availabilityCheck?.can_extend_earlier ?? false,
                    later: availabilityCheck?.can_extend_later ?? false
                })
            }
            fetchData();
        }

    }, [props.selectedInterval.start, props.selectedInterval.end]);

    const isSelectedSlot = (slot) => {
        if (!props.selectedInterval) {
            return false;
        }

        return (
            slot.start >= props.selectedInterval.start
            && slot.end <= props.selectedInterval.end
        )
    }

    const clearAllSlots = () => {
        if (!props.disallowClear) {
            props.onChange(null);
            setCanExtend({earlier: false, later: false});
        }
    }

    const userHasSelectedSlot = () => {
        return (props.selectedInterval && props.selectedInterval.start);
    }

    /**
     * Should a particular slot be displayed?
     * Note that there is an almost equivalent function in SingleAvailabilitySlotSelector
     * @param slot
     * @returns {boolean}
     */
    const shouldShowSlot = (slot) => {
        if (isSelectedSlot(slot)) {
            return false;
        }

        return isSameDay(dateFromState(slot.start), dateFromState(props.bookingDate));
    }

    const shouldShowExtendButtons = () => {
        if (props.bookingConfig.okkami_id) {
            return false;
        }
        return true;
    }

    const handleExtendEarlier = () => {
        const earlierSlot = getEarlierSlot(
            props.selectedInterval,
            props.availability.availableIntervals,
            props.bookingConfig.duration
        );
        props.onChange({
            start: earlierSlot.start,
            end: props.selectedInterval.end
        })
    }

    const handleExtendLater = () => {
        const laterSlot = getLaterSlot(
            props.selectedInterval,
            props.availability.availableIntervals,
            props.bookingConfig.duration
        );
        props.onChange({
            start: props.selectedInterval.start,
            end: laterSlot.end
        })
    }

    if (!props.availability) {
        return null;
    }

    return (<>
        { userHasSelectedSlot() && (<>
            <div className="availability-container">
                <div>
                    {shouldShowExtendButtons() &&
                        <AvailabilityButtonConsecutive
                            type="earlier"
                            canExtend={canExtend.earlier}
                            onClick={handleExtendEarlier}
                        />
                    }

                    <AvailabilityButton
                        date={props.selectedInterval.start ?? null}
                        availability={props.selectedInterval}
                        onClick={clearAllSlots}
                        hideTooltip
                        active
                    />

                    {shouldShowExtendButtons() &&
                        <AvailabilityButtonConsecutive
                            type="later"
                            canExtend={canExtend.later}
                            onClick={handleExtendLater}
                            className="margin-right-0"
                        />
                    }

                    {(!props.disallowClear) && (
                        <button
                            type="button"
                            className="button availability-cancel clear"
                            onClick={clearAllSlots}
                            style={{
                                padding: '0.9rem 1rem 0.5rem',
                            }}
                        >
                            Clear
                        </button>
                    )}
                </div>
                {props.showExtendButtons && (
                    <div className="message">
                        Add more time before or after as necessary
                    </div>
                )}
            </div>
        </>)}

        { (!userHasSelectedSlot()) && (
            <div className="availability-container">
                { props.availability.availableIntervals.map((slot, index) => {
                    if (shouldShowSlot(slot)) {
                        return (
                            <AvailabilityButton
                                key={index}
                                availability={slot}
                                onClick={props.onChange}
                            />
                        )
                    }
                })}
            </div>
        )}
    </>);
}

AmenityConsecutiveAvailabilityInput.propTypes = {
    bookingConfig: PropTypes.object,
    availability: PropTypes.object,
    duration: PropTypes.number,
    disallowClear: PropTypes.bool,
}

AmenityConsecutiveAvailabilityInput.defaultProps = {
    disallowClear: false,
    showExtendButtons: true
}

export default AmenityConsecutiveAvailabilityInput;
